import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

// ---------------------------------------------------------------------------
// Wave 5 — page-editor work panel localization.
//
// WHY THIS EXISTS: the 2026-08-18 slice-5A IDE-editor census
// (`docs/superpowers/specs/2026-08-18-slice5-ide-editors.md`, §1 "i18n")
// measured the `page-work-panel` region under the APPLIANCE profile — which
// runs `VITE_COGO_DEFAULT_LOCALE=ko` — and read 29 body lines, **0 Korean**,
// 25 English-only. The surrounding IDE shell is fully Korean (the rail reads
// 페이지 / 데이터 / 동작), so an appliance operator opens the page editor and
// the chrome changes language mid-screen. That reading was re-derived at
// `34dbcdc9a` with the census's own instrument and was unchanged: 29 / 0 / 25.
//
// The two consumers are `components/shell/editor-shell.tsx` (the two tab
// labels + the panel's aria-labels) and
// `components/inspector/property-inspector.tsx` (the whole body). This module
// is the single locale-keyed home for both, following the `COPY` table +
// `get…Copy(locale)` shape the repo already uses in `admin-page-copy.ts` and
// `studio-publish-deploy-copy.ts`. It sits at the `features/page-editor/`
// root rather than beside one of the two because both read it.
//
// `en` is byte-identical to the literals these files rendered before this
// change. That is deliberate: `DEFAULT_LOCALE` is `en`, and the existing
// `editor-shell-work-panel.test.tsx` suite resolves controls by their English
// accessible name (`getByRole('button', { name: 'Add UI' })`). A drift in `en`
// shows up as that suite failing, not as a silent regression.
//
// TERMINOLOGY — checked against the Korean already in the product rather than
// invented (per the project's "keep the established rendering" rule):
//  - `블록` (block), `페이지` (page), `경로` (route), `설명` (description),
//    `미리보기` (preview) all already appear in the Korean rail/dictionary
//    tables; they are reused verbatim here.
//  - **`Flow` in this panel is NOT an ActionFlow.** It is one of the two page
//    LAYOUT modes (`flow` vs `absolute`, `property-inspector.tsx:793-797`).
//    Rendering it `플로우` — the established Korean for the ActionFlow concept
//    (`flow: "플로우"` in the studio dictionary) — would collide two unrelated
//    concepts on adjacent surfaces, since the same operator's 동작 rail item
//    genuinely does mean ActionFlow. It is rendered as a layout term instead:
//    `자동 배치` / `자유 배치`, and the same split is carried in ja/zh/zh-TW.
//
// KEPT VERBATIM — deliberately NOT in this table:
//  - `activePage.id` / `activePage.route` and the `{MOD}` modifier glyph.
//    Identifiers and a keyboard key, not copy.
//  - The `/page-route` route placeholder — a path FORMAT example the operator
//    types over; translating it would suggest a non-ASCII route is expected.
// ---------------------------------------------------------------------------

export interface PageWorkPanelCopy {
  /** Work-panel tab labels + the panel chrome's accessible names. */
  detailsTab: string;
  addUiTab: string;
  closeDetails: string;
  closeWorkPanel: string;
  layers: string;
  canvas: string;
  detailsShort: string;

  /** Ask-Coogo operation labels rendered inside the panel. */
  reviewPage: string;
  rename: string;

  /** Section headings. */
  identity: string;
  layout: string;
  preview: string;
  pageInfo: string;
  pageActions: string;

  /** Identity fields. */
  pageId: string;
  title: string;
  description: string;
  route: string;
  descriptionPlaceholder: string;
  pageTitleAria: string;
  pageDescriptionAria: string;
  pageIdRequired: string;
  pageIdExists: string;

  /** Layout modes — page layout, NOT ActionFlow. See header note. */
  layoutAria: string;
  layoutFlow: string;
  layoutFreeform: string;

  /** Preview. */
  colorScheme: string;
  light: string;
  dark: string;

  /** Page info + actions. */
  blocks: string;
  defaultRoute: string;
  setAsDefaultPage: string;
  duplicatePage: string;
  deletePage: string;
  cancel: string;
  delete: string;

  /** Footer hints. */
  clickToEdit: string;
  holdToMultiSelect: (mod: string) => string;
  loadTemplateToEdit: string;
}

const en: PageWorkPanelCopy = {
  detailsTab: "Page details",
  addUiTab: "Add UI",
  closeDetails: "Close details",
  closeWorkPanel: "Close page work panel",
  layers: "Layers",
  canvas: "Canvas",
  detailsShort: "Details",

  reviewPage: "Review page",
  rename: "Rename",

  identity: "Identity",
  layout: "Layout",
  preview: "Preview",
  pageInfo: "Page Info",
  pageActions: "Page actions",

  pageId: "Page ID",
  title: "Title",
  description: "Description",
  route: "Route",
  descriptionPlaceholder: "Page description...",
  pageTitleAria: "Page title",
  pageDescriptionAria: "Page description",
  pageIdRequired: "Page ID is required",
  pageIdExists: "Page ID already exists",

  layoutAria: "Page layout",
  layoutFlow: "Flow",
  layoutFreeform: "Freeform",

  colorScheme: "Color Scheme",
  light: "Light",
  dark: "Dark",

  blocks: "Blocks",
  defaultRoute: "Default Route",
  setAsDefaultPage: "Set as default page",
  duplicatePage: "Duplicate page",
  deletePage: "Delete page",
  cancel: "Cancel",
  delete: "Delete",

  clickToEdit: "Click on a block or element to edit properties",
  holdToMultiSelect: (mod) => `Hold ${mod} to multi-select`,
  loadTemplateToEdit: "Load a template to start editing.",
};

const ko: PageWorkPanelCopy = {
  detailsTab: "페이지 설정",
  addUiTab: "UI 추가",
  closeDetails: "설정 닫기",
  closeWorkPanel: "페이지 작업 패널 닫기",
  layers: "레이어",
  canvas: "캔버스",
  detailsShort: "설정",

  reviewPage: "페이지 검토",
  rename: "이름 변경",

  identity: "식별 정보",
  layout: "레이아웃",
  preview: "미리보기",
  pageInfo: "페이지 정보",
  pageActions: "페이지 작업",

  pageId: "페이지 ID",
  title: "제목",
  description: "설명",
  route: "경로",
  descriptionPlaceholder: "페이지 설명...",
  pageTitleAria: "페이지 제목",
  pageDescriptionAria: "페이지 설명",
  pageIdRequired: "페이지 ID를 입력하세요",
  pageIdExists: "이미 있는 페이지 ID입니다",

  layoutAria: "페이지 레이아웃",
  layoutFlow: "자동 배치",
  layoutFreeform: "자유 배치",

  colorScheme: "색상 모드",
  light: "라이트",
  dark: "다크",

  blocks: "블록",
  defaultRoute: "기본 경로",
  setAsDefaultPage: "기본 페이지로 지정",
  duplicatePage: "페이지 복제",
  deletePage: "페이지 삭제",
  cancel: "취소",
  delete: "삭제",

  clickToEdit: "블록이나 요소를 클릭하면 속성을 편집할 수 있습니다",
  holdToMultiSelect: (mod) => `${mod} 를 누른 채 클릭하면 여러 개를 선택합니다`,
  loadTemplateToEdit: "템플릿을 불러오면 편집을 시작할 수 있습니다",
};

const ja: PageWorkPanelCopy = {
  detailsTab: "ページ設定",
  addUiTab: "UI を追加",
  closeDetails: "設定を閉じる",
  closeWorkPanel: "ページ作業パネルを閉じる",
  layers: "レイヤー",
  canvas: "キャンバス",
  detailsShort: "設定",

  reviewPage: "ページを確認",
  rename: "名前を変更",

  identity: "識別情報",
  layout: "レイアウト",
  preview: "プレビュー",
  pageInfo: "ページ情報",
  pageActions: "ページ操作",

  pageId: "ページ ID",
  title: "タイトル",
  description: "説明",
  route: "ルート",
  descriptionPlaceholder: "ページの説明...",
  pageTitleAria: "ページタイトル",
  pageDescriptionAria: "ページの説明",
  pageIdRequired: "ページ ID を入力してください",
  pageIdExists: "そのページ ID は既に使われています",

  layoutAria: "ページレイアウト",
  layoutFlow: "自動配置",
  layoutFreeform: "自由配置",

  colorScheme: "カラーモード",
  light: "ライト",
  dark: "ダーク",

  blocks: "ブロック",
  defaultRoute: "既定のルート",
  setAsDefaultPage: "既定のページに設定",
  duplicatePage: "ページを複製",
  deletePage: "ページを削除",
  cancel: "キャンセル",
  delete: "削除",

  clickToEdit: "ブロックまたは要素をクリックするとプロパティを編集できます",
  holdToMultiSelect: (mod) => `${mod} を押しながらクリックで複数選択できます`,
  loadTemplateToEdit: "テンプレートを読み込むと編集を開始できます",
};

const zh: PageWorkPanelCopy = {
  detailsTab: "页面设置",
  addUiTab: "添加 UI",
  closeDetails: "关闭设置",
  closeWorkPanel: "关闭页面工作面板",
  layers: "图层",
  canvas: "画布",
  detailsShort: "设置",

  reviewPage: "检查页面",
  rename: "重命名",

  identity: "标识信息",
  layout: "布局",
  preview: "预览",
  pageInfo: "页面信息",
  pageActions: "页面操作",

  pageId: "页面 ID",
  title: "标题",
  description: "描述",
  route: "路由",
  descriptionPlaceholder: "页面描述...",
  pageTitleAria: "页面标题",
  pageDescriptionAria: "页面描述",
  pageIdRequired: "请填写页面 ID",
  pageIdExists: "该页面 ID 已存在",

  layoutAria: "页面布局",
  layoutFlow: "流式排列",
  layoutFreeform: "自由排列",

  colorScheme: "配色方案",
  light: "浅色",
  dark: "深色",

  blocks: "区块",
  defaultRoute: "默认路由",
  setAsDefaultPage: "设为默认页面",
  duplicatePage: "复制页面",
  deletePage: "删除页面",
  cancel: "取消",
  delete: "删除",

  clickToEdit: "点击区块或元素即可编辑其属性",
  holdToMultiSelect: (mod) => `按住 ${mod} 点击可多选`,
  loadTemplateToEdit: "加载模板即可开始编辑",
};

const zhTW: PageWorkPanelCopy = {
  detailsTab: "頁面設定",
  addUiTab: "新增 UI",
  closeDetails: "關閉設定",
  closeWorkPanel: "關閉頁面工作面板",
  layers: "圖層",
  canvas: "畫布",
  detailsShort: "設定",

  reviewPage: "檢查頁面",
  rename: "重新命名",

  identity: "識別資訊",
  layout: "版面配置",
  preview: "預覽",
  pageInfo: "頁面資訊",
  pageActions: "頁面操作",

  pageId: "頁面 ID",
  title: "標題",
  description: "說明",
  route: "路由",
  descriptionPlaceholder: "頁面說明...",
  pageTitleAria: "頁面標題",
  pageDescriptionAria: "頁面說明",
  pageIdRequired: "請填寫頁面 ID",
  pageIdExists: "此頁面 ID 已存在",

  layoutAria: "頁面版面配置",
  layoutFlow: "流式排版",
  layoutFreeform: "自由排版",

  colorScheme: "色彩配置",
  light: "淺色",
  dark: "深色",

  blocks: "區塊",
  defaultRoute: "預設路由",
  setAsDefaultPage: "設為預設頁面",
  duplicatePage: "複製頁面",
  deletePage: "刪除頁面",
  cancel: "取消",
  delete: "刪除",

  clickToEdit: "點選區塊或元件即可編輯其屬性",
  holdToMultiSelect: (mod) => `按住 ${mod} 點選可多重選取`,
  loadTemplateToEdit: "載入範本即可開始編輯",
};

const COPY: Record<LocaleCode, PageWorkPanelCopy> = {
  en,
  ko,
  ja,
  zh,
  "zh-TW": zhTW,
};

export function getPageWorkPanelCopy(locale: LocaleCode): PageWorkPanelCopy {
  return COPY[locale];
}
